import { Configuration, UsersApi } from '@/api';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useSettings } from '@/contexts/SettingsContext';
import { User } from '@/models/User';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import CurrencyForm, { CurrencyFormData } from '@/modules/settings/components/CurrencyForm';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import useUserQuery from '@/modules/settings/hooks/useUserQuery';
import { SupplierHomePath } from '@/modules/supplier/paths';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

const Currency = () => {
  const { user, isLoading } = useUserQuery();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const usersApi = new UsersApi(new Configuration(useAuth));

  const { currencyCode, resetSettings } = useSettings();
  const returnTo = searchParams.get('returnTo');

  const handleSubmitCurrency = async (user: User, currency: CurrencyFormData) => {
    if (currency.value === undefined) {
      return;
    }

    try {
      await usersApi.updateUser(user.id, {
        ...user,
        currencyCode: currency.value,
      });

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={'Currency settings updated successfully!'}
          visible={t.visible}
        />
      ));

      await resetSettings();

      if (returnTo === SupplierHomePath) {
        navigate(returnTo);
      }
    } catch (error) {
      console.error('Error while updating user currency:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!user) {
    return;
  }

  return (
    <>
      <SettingsHeader title="Currency" icon={<CurrencyDollarIcon className="w-6 h-6" />} />

      <CurrencyForm
        value={currencyCode}
        onSubmit={(value) => handleSubmitCurrency(user, value)}
        hideLabel
        description={
          <span className="block mt-4 text-gray-700 text-sm">
            Choose your preferred currency from the options above.
          </span>
        }
      />
    </>
  );
};

export default Currency;
